/**
 * adapty_product_links 域 Zod 共享契约 —— Adapty 内部 product_id ↔ 我方逻辑产品 access_level_id 的手动关联
 *
 * 契约源：admin-own 新契约（dev-docs 收录义务，同步提 PR）。
 *
 * 设计：
 * - server-side getPaywall 不返 access_level_id → 镜像产品(adapty_product_id) 与 product_metadata(按 access_level_id 归组)
 *   之间缺一条边，由运营手动关联补齐（替掉按标题启发式判「已录入」的弱校验）。
 * - 一个 adapty_product_id 一条 link → 一个 access_level（1:1 逻辑产品）；access_level 侧可多对一（A/B 变体）。
 * - access_level_id 存运营断言字符串、无 FK（与 product_metadata 行是否已录入解耦）；同 product_metadata 的
 *   access_level_id 口径：非空、1..255、无空白（exact-match 键）。
 */

import { z } from 'zod';

/** access_level_id —— 关联目标逻辑产品键；非空、1..255、无空白（与 product_metadata.access_level_id 同口径 exact-match 键）*/
const LinkAccessLevelIdSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^\S+$/, 'access_level_id must not contain whitespace');

/** Adapty 内部 product_id（server-side 镜像 UUID）；非空、1..255、无空白 */
const AdaptyProductIdSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^\S+$/, 'adapty_product_id must not contain whitespace');

/**
 * 建立固定关联（A8 §8.3）：按 (app_id, adapty_product_id) 关联到 access_level_id。
 * 已有 active 关联时同值 no-op、异值 RESOURCE_CONFLICT（不覆盖）；无 active 时只能复活该产品历史关联值（含软删行，
 * 异值 RESOURCE_CONFLICT），从未关联过的产品才新建。adapty_title 为关联时的 Adapty 标题快照（可选、仅展示）。
 */
export const UpsertAdaptyProductLinkRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    adapty_product_id: AdaptyProductIdSchema,
    access_level_id: LinkAccessLevelIdSchema,
    /** 关联时的 Adapty title 快照（仅展示辅助，非键）*/
    adapty_title: z.string().max(255).nullable().optional(),
  })
  // .strict()：拒未知键（typo 直接 422，不静默 strip）
  .strict();
export type UpsertAdaptyProductLinkRequest = z.infer<typeof UpsertAdaptyProductLinkRequestSchema>;

/** adapty_product_links 响应 DTO（DB 列名 → API 字段；不含软删行）*/
export type AdaptyProductLinkResponse = {
  id: string;
  app_id: string;
  /** Adapty 内部 product_id（镜像 UUID）*/
  adapty_product_id: string;
  /** 关联到的逻辑产品 = product_metadata.access_level_id */
  access_level_id: string;
  /** 关联时的 Adapty title 快照（仅展示辅助）；null = 未快照 */
  adapty_title: string | null;
  created_at: string;
  updated_at: string;
};
